import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Typewriter } from "../components/Typewriter";
import axios from "axios";
import { API_ENDPOINTS } from '../config/api';
import { trackEvent } from '../config/amplitude';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

// Commented out floating elements
/*
interface FloatingElement {
  id: number;
  top: string;
  left: string;
  duration: string;
  fontSize: string;
  content: string;
}
*/

export const LandingPage = () => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // const [floatingElements, setFloatingElements] = useState<FloatingElement[]>([]);

  // Commented out floating elements effect
  /*
  useEffect(() => {
    const elements = [
      {
        id: 0,
        top: `${Math.random() * 70 + 15}vh`, // Keep elements in middle 70% of screen
        left: `${Math.random() * 70 + 15}vw`,
        duration: `${6 + Math.random() * 3}s`,
        fontSize: `${14}px`,
        content: "🔥 Hot Post"
      },
      {
        id: 1,
        top: `${Math.random() * 70 + 15}vh`,
        left: `${Math.random() * 70 + 15}vw`,
        duration: `${6 + Math.random() * 3}s`,
        fontSize: `${14}px`,
        content: "⬆️ 1.2k"
      }
    ];
    setFloatingElements(elements);
  }, []);
  */

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const isUrl = inputValue.startsWith("http://") || inputValue.startsWith("https://");
      const payload = isUrl ? { url: inputValue } : { text: inputValue };
      
      const keywordResponse = await axios.post(API_ENDPOINTS.EXTRACT_KEYWORDS, payload);

      const keywords = keywordResponse.data.keywords;
      
      // Track the event before navigation
      trackEvent('Product Submitted', {
        inputType: isUrl ? 'url' : 'text',
        keywordsCount: keywords.length,
        keywords: keywords,
        sourceInput: inputValue
      });

      navigate("/feed", { state: { keywords, sourceInput: inputValue } });
    } catch (error) {
      console.error("Error extracting keywords:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-gradient-to-br from-gradientStart to-gradientEnd animate-gradient">
      {/* Logo in top left corner */}
      <div className="absolute top-4 left-6 z-20 flex items-center gap-4">
        <h1 className="text-xl font-bold text-slate-800">
          Spriglaunch
        </h1>
        <a 
          href="https://spriglaunch.com/feed"
          className="text-base text-slate-700 hover:text-cyan-600 transition-colors mt-0.5"
          target="_self"
        >
          Feed
        </a>
      </div>

      {/* Commented out floating elements background */}
      {/*
      <div className="absolute inset-0 pointer-events-none">
        {floatingElements.map((element) => (
          <motion.div
            key={element.id}
            className="absolute bg-cyan-100 text-cyan-600 px-2 py-1 rounded-md shadow-md opacity-60"
            initial={{ y: 0, opacity: 0.6 }}
            animate={{
              y: [-10, -30, -10],
              opacity: [0.6, 0.9, 0.6],
            }}
            transition={{
              duration: parseFloat(element.duration),
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              top: element.top,
              left: element.left,
              fontSize: element.fontSize,
            }}
          >
            {element.content}
          </motion.div>
        ))}
      </div>
      */}

      <div className="max-w-xl w-full mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8"
        >
          {/* Lottie Animation */}
          <div className="w-48 h-48 mx-auto mb-0">
            <DotLottieReact
              src="https://lottie.host/a2db0f13-a931-4b67-b2e7-526b8a3e7f3e/hikiaRFkLH.lottie"
              loop
              autoplay
            />
          </div>
          
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-slate-700 animate-pulse-slow"
          >
            Build Fast, Grow Faster
          </motion.p>
          
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            className="text-lg text-white mt-2 opacity-90 font-medium"
          >
            Enter your product link or idea to instantly find all the related keywords and Reddit posts
          </motion.p>
          
          </motion.div>

          <form onSubmit={handleSubmit} className="w-full">
            <div className="relative">
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e as any);
                  }
                }}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                rows={3}
              />
              {!inputValue && (
                <div className="absolute inset-0 flex items-start px-4 pt-2 pointer-events-none text-slate-500">
                  <Typewriter 
                    texts={["AI code editor", "https://cursor.com"]} 
                    typingSpeed={150}
                    erasingSpeed={100}
                    delayBeforeErase={2000}
                    delayBetweenTexts={1000}
                  />
                </div>
              )}
            </div>

            {/* Submit button moved below textarea */}
            <div className="mt-4 flex justify-end">
              <motion.button
                type="submit"
                disabled={!inputValue || isLoading}
                whileTap={{ scale: 0.9 }}
                whileHover={{ scale: 1.05 }}
                initial={{ scale: 1 }}
                animate={{ scale: 1 }}
                className="relative bg-cyan-600 text-white px-6 py-3 rounded-lg hover:bg-cyan-700 transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] overflow-hidden"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Submit
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </motion.button>
            </div>
          </form>
      </div>

      <style>
        {`
          @keyframes gradientBG {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }

          .animate-gradient {
            background-size: 200% 200%;
            animation: gradientBG 8s ease infinite;
          }
        `}
      </style>
    </div>
  );
};
